import { api } from '@/api';
import { getLanguage } from '@/i18n';
import type {
  BookingOfferDTO,
  MarkVoucherForPaymentPayloadDTO,
  MultiRideAccountDTO,
  PlaceSelectionDTO,
  PromotionCodeDTO,
  ReductionCardAccountDTO,
  TravelPassAccountDTO,
  ValidateAdyenCheckoutPaymentDetailsBodyDTO,
} from '@/types/dto/agent';
import type { SearchFormValues } from '@turnit-ride-ui/webshop-search-widget';
import download from 'downloadjs';

export const downloadBookingTickets = async (
  bookingId: string,
  bookingNumber?: string
) => {
  const response = await api.agentApi.Documents_GetBookingTickets(
    {
      includeAll: true,
      bookingId,
    },
    {},
    {
      responseType: 'blob',
      headers: {
        'Accept-Language': getLanguage(),
      },
    }
  );

  download(response.data, `Tickets_${bookingNumber}`, 'pdf');
};

export const downloadTicketDocumentByType = async (
  fulfillment: { id: string; isOutBound?: boolean },
  documentType: 'PKPASS' | 'TICKET',
  bookingNumber?: string
) => {
  const [filename, mimeType] =
    documentType === 'PKPASS'
      ? [`Tickets_${bookingNumber}.pkpass`, 'application/vnd.apple.pkpass']
      : [`Tickets_${bookingNumber}.pdf`, 'application/pdf'];

  const response = await api.agentApi.Documents_GetDocument(
    {
      fulfillmentId: fulfillment.id,
      documentType,
      ...(typeof fulfillment.isOutBound === 'boolean' && {
        isForInbound: !fulfillment.isOutBound,
      }),
    },
    {},
    {
      responseType: 'blob',
      headers: {
        'Accept-Language': getLanguage(),
      },
    }
  );

  if (response.data instanceof Blob && response.data.size > 0) {
    download(response.data, filename, mimeType);
  } else {
    throw new Error('Empty or invalid document received');
  }
};

export const sendPurchaseConfirmation = async (
  bookingId: string,
  emailsOverride: Array<string>
) => {
  return (
    await api.agentApi.Notifications_SendPurchaseConfirmation(null, {
      bookingId,
      emailsOverride,
      includeTickets: true,
    })
  ).data;
};

export const createBooking = async (
  offers: Array<BookingOfferDTO>,
  passengers: SearchFormValues['passengers'],
  promotionCodes?: Array<PromotionCodeDTO>
) => {
  return (
    await api.agentApi.Bookings_CreateBooking(null, {
      offers,
      passengerSpecifications: passengers.map(
        ({ age, externalReference, type }) => ({
          age,
          externalReference,
          type,
        })
      ),
      promotionCodes,
    })
  ).data;
};

export const fetchBooking = async (id: string | null | undefined) => {
  const data = (await api.agentApi.Bookings_GetBooking(id)).data;

  return {
    ...data,
    passengers: data.passengers?.sort(({ age }) => (!age ? -1 : 0)),
  };
};

export const fetchPlaceAvailability = async (
  bookingId: string,
  reservationId: string
) => {
  return (
    await api.agentApi.Bookings_GetAvailabilitiesPlaceMap({
      bookingId,
      reservationId,
    })
  ).data;
};

export const fetchFloorPlanIcons = async () => {
  return (await api.agentApi.FloorPlanIcons_GetFloorPlanIcons()).data;
};

export const updatePlaceSelections = async (
  bookingId: string,
  placeSelections: Array<PlaceSelectionDTO>
) => {
  return (
    await api.agentApi.Bookings_UpdateBooking(bookingId, {
      placeSelections,
    })
  ).data;
};

export const undoSeatSelection = async (
  bookingId: string,
  placeSelectionFeeId: string
) => {
  return (
    await api.agentApi.Bookings_DeletePlaceSelectionFee({
      bookingId,
      id: placeSelectionFeeId,
    })
  ).data;
};

export const validateVoucher = async (
  voucherCode: string,
  bookingId: string
) => {
  return (
    await api.agentApi.Payments_ValidateVoucherForBooking(
      { bookingId, voucherCode },
      {
        offerPartIds: [],
      }
    )
  ).data;
};

export const addVoucherToBooking = async (
  voucherCode: string,
  bookingId: string,
  paidAmount: MarkVoucherForPaymentPayloadDTO['paidAmount']
) => {
  return (
    await api.agentApi.Payments_MarkVoucherForPayment(
      { bookingId, voucherCode },
      { offerPartIds: [], paidAmount }
    )
  ).data;
};

export const removeVoucherFromBooking = async (
  voucherCode: string,
  bookingId: string
) => {
  return (
    await api.agentApi.Payments_DeleteVoucherMarkedForPayment(
      { bookingId, voucherCode },
      { offerPartIds: [] }
    )
  ).data;
};

export const fetchIssuer = async ({
  controlNumber,
  issuer,
}: {
  controlNumber: string;
  issuer: string;
}) => {
  // as { travelAccount?: ... } can be removed once
  // TurnitOsdmContractsTravelAccountsTravelAccountResponse.travelAccount?: null & (...)
  // is changed to
  // TurnitOsdmContractsTravelAccountsTravelAccountResponse.travelAccount?: (...)
  // current schema causes 'travelAccount' to be type 'never'
  return (
    await api.agentApi.TravelAccounts_GetTravelAccount({
      Issuer: issuer,
      TravelAccount: controlNumber,
      Embed: 'ALL',
      cancelledIncluded: true,
    })
  ).data as {
    travelAccount?:
      | MultiRideAccountDTO
      | TravelPassAccountDTO
      | ReductionCardAccountDTO;
  };
};

export const validateAdyenCheckoutPaymentDetails = async (
  bookingId: string,
  body: ValidateAdyenCheckoutPaymentDetailsBodyDTO
) => {
  return await api.agentApi.Payments_ValidateAdyenCheckoutPaymentDetails(
    { bookingId },
    body
  );
};

export const getBookingPaymentStatus = async (bookingId: string) => {
  const response = await api.agentApi.Payments_GetBookingPaymentStatus({
    bookingId,
  });

  return response.data.status;
};
